import type { TankReadingsOverviewRow } from '@/api/hooks/useTankReadings'
import {
  type DataPerspective,
  getPerspectiveLabels,
  statusForRow,
} from '@/api/hooks/useTankReadings'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import {
  LastReceivedRenderer,
  StatusRenderer,
} from './TankReadingsCellRenderers'
import { formatReadingVolume, tankProductLabel } from './tankReadingsHelpers'

// Labels for the Status set-filter dropdown (the column value is the raw level).
const STATUS_FILTER_LABELS: Record<string, string> = {
  ok: 'OK',
  stale: 'Stale',
  late: 'Late',
  none: 'No Readings',
}

/**
 * Column definitions for the Tank Readings overview grid (flat, one row per
 * tank). Partner, Site, and the identity/location columns read through the
 * selected Data Format perspective; reading facts are perspective-independent.
 */
export function tankReadingsColumnDefs(
  perspective: DataPerspective,
): ColDef<TankReadingsOverviewRow>[] {
  const labelsFor = (row: TankReadingsOverviewRow) =>
    getPerspectiveLabels(row, perspective)

  const columns: ColDef<TankReadingsOverviewRow>[] = [
    // Only perspective-independent columns are server-sortable/-filterable; their
    // colId must match the backend _SORT_KEYS / _FILTER_VALUE_FUNCS. Perspective-
    // dependent label columns (site, tank, address, city, state) are left without
    // sort/filter because the active perspective is a client-side display choice
    // with no single server value; the search box covers them.
    {
      headerName: 'Partner',
      colId: 'partner',
      sortable: true,
      filter: 'agTextColumnFilter',
      minWidth: 160,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data?.partner_tenant_name,
    },
    {
      headerName: 'Site',
      colId: 'site',
      minWidth: 160,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? labelsFor(p.data).site_name : undefined,
    },
    {
      headerName: 'Tank – Product',
      colId: 'tank_product',
      minWidth: 180,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? tankProductLabel(labelsFor(p.data)) : undefined,
    },
    {
      headerName: 'Volume',
      colId: 'volume',
      sortable: true,
      filter: 'agNumberColumnFilter',
      minWidth: 120,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? formatReadingVolume(p.data.reading) : undefined,
    },
    {
      headerName: 'Measured Time',
      colId: 'measured_time',
      sortable: true,
      minWidth: 180,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        formatCompactDateTime(p.data?.reading?.measured_time),
    },
    {
      headerName: 'Last Received',
      colId: 'last_received',
      sortable: true,
      minWidth: 140,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data?.reading?.received_at ?? null,
      cellRenderer: (p: ICellRendererParams<TankReadingsOverviewRow>) =>
        p.data ? (
          <LastReceivedRenderer
            receivedAt={p.data.reading?.received_at}
            level={statusForRow(p.data)}
          />
        ) : null,
    },
    {
      headerName: 'Status',
      colId: 'status',
      minWidth: 130,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['ok', 'stale', 'late', 'none'],
        valueFormatter: (p: ValueFormatterParams) =>
          STATUS_FILTER_LABELS[p.value] ?? p.value,
      },
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? statusForRow(p.data) : undefined,
      cellRenderer: (p: ICellRendererParams<TankReadingsOverviewRow>) =>
        p.data ? <StatusRenderer level={statusForRow(p.data)} /> : null,
    },
    {
      headerName: 'Address',
      colId: 'address',
      minWidth: 180,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? labelsFor(p.data).address : undefined,
    },
    {
      headerName: 'City',
      colId: 'city',
      minWidth: 120,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? labelsFor(p.data).city : undefined,
    },
    {
      headerName: 'State',
      colId: 'state',
      minWidth: 90,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? labelsFor(p.data).state : undefined,
    },
    {
      headerName: 'Postal Code',
      colId: 'postal_code',
      minWidth: 110,
      valueGetter: (p: ValueGetterParams<TankReadingsOverviewRow>) =>
        p.data ? labelsFor(p.data).postal_code : undefined,
    },
  ]

  // Default every column to no filter (column-level beats GraviGrid's base
  // defaultColDef); the three columns above that set an explicit filter keep it.
  return columns.map((col) => ({ filter: false, ...col }))
}
